import {
  GridOrderDirection,
  GridType,
  IntervalsAxisConfig,
  IntervalsConfig,
} from '@editor/interfaces/inspectionItemGrid'
import { LocalStorageManager } from '@editor/services/localStorage'

import { calculateInterval, calculateMaxPointCount } from './utils'

/**
 * Geometry-independent grid spacing preferences that are remembered across grids.
 * Values that depend on the selected plane's size (total, offset, interval.value) are
 * intentionally excluded - they must stay computed per volume.
 */
interface GridIntervalAxisPrefs {
  /** Edge distance from the plane edge to the first point (mm), for edge-based grids. */
  edgeDistance: number
  /** Number of points along the axis, for edge-based grids. */
  pointCount: number
}

export interface GridIntervalPrefs {
  type: GridType
  orderDirection: GridOrderDirection
  longAxis: GridIntervalAxisPrefs
  shortAxis: GridIntervalAxisPrefs
}

const gridIntervalPrefsManager = new LocalStorageManager<GridIntervalPrefs | null>('grid-interval-prefs', null)

const clamp = (value: number, min: number, max: number): number => Math.min(Math.max(value, min), max)

/**
 * Extract the geometry-independent preferences from a full interval config.
 */
const extractGridIntervalPrefs = (config: IntervalsConfig): GridIntervalPrefs => ({
  type: config.type,
  orderDirection: config.orderDirection,
  longAxis: {
    edgeDistance: config.longAxis.edgeDistance.value,
    pointCount: config.longAxis.pointCount.value,
  },
  shortAxis: {
    edgeDistance: config.shortAxis.edgeDistance.value,
    pointCount: config.shortAxis.pointCount.value,
  },
})

/**
 * Persist the user's grid spacing preferences derived from the given config.
 */
export const saveGridIntervalPrefs = (config: IntervalsConfig) => {
  gridIntervalPrefsManager.save(extractGridIntervalPrefs(config))
}

/**
 * Remove any stored grid spacing preferences (used by 全てをリセット).
 */
export const clearGridIntervalPrefs = () => {
  gridIntervalPrefsManager.clear()
}

/**
 * Overlay the stored preferences onto a freshly-computed default config.
 * Restored values are clamped to the current plane's axis limits and, for edge-based
 * grids, the per-axis interval is recomputed the same way the panel does.
 *
 * Returns the config unchanged when no preferences are stored.
 */
export const applyGridIntervalPrefs = (config: IntervalsConfig): IntervalsConfig => {
  const prefs = gridIntervalPrefsManager.load()
  if (!prefs) return config

  const applyAxis = (axis: IntervalsAxisConfig, axisPrefs: GridIntervalAxisPrefs): IntervalsAxisConfig => {
    const edgeDistanceValue = clamp(axisPrefs.edgeDistance, axis.edgeDistance.min, axis.edgeDistance.max)
    const pointCountMax = calculateMaxPointCount(axis.total, edgeDistanceValue)
    const pointCountValue = clamp(axisPrefs.pointCount, axis.pointCount.min, pointCountMax)

    return {
      ...axis,
      edgeDistance: { ...axis.edgeDistance, value: edgeDistanceValue },
      pointCount: { ...axis.pointCount, max: pointCountMax, value: pointCountValue },
      interval:
        prefs.type === GridType.EdgeBased
          ? { ...axis.interval, value: calculateInterval(axis.total, edgeDistanceValue, pointCountValue) }
          : axis.interval,
    }
  }

  return {
    ...config,
    type: prefs.type,
    orderDirection: prefs.orderDirection,
    longAxis: applyAxis(config.longAxis, prefs.longAxis),
    shortAxis: applyAxis(config.shortAxis, prefs.shortAxis),
  }
}
